import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareText } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  LanguageRow,
  ListResponse,
  ManagedCircumscriptionRow,
  NotificationsResponse,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";

/**
 * The scheduling parameters, in the order the old grid showed them. Kept as one
 * list so the table columns and the editor cannot drift apart — every one of
 * them is a whole number of weeks or months and carries a hint of its own.
 */
const PARAMETERS = [
  "rewind_weeks",
  "forward_weeks",
  "min_speaker_re_req_months",
  "apply_sp_re_req_min_assign_count",
  "forward_sp_assign_weeks",
  "min_weeks_whitout_sp_assign",
  "max_weeks_whitout_sp_assign",
  "min_weeks_whitout_sp_assign_sm",
  "max_weeks_whitout_sp_assign_sm",
] as const;

type Parameter = (typeof PARAMETERS)[number];

/**
 * Label and hint per parameter, spelled out rather than assembled from the name.
 * `t(`…${name}`)` would read better here, but scripts/check-i18n.mjs can only
 * see literal keys — a computed one is skipped silently, and these eighteen
 * would sit outside the guard.
 */
function useParameterLabels(): Record<Parameter, { label: string; hint: string }> {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      rewind_weeks: {
        label: t("activerecord.attributes.circumscription.rewind_weeks"),
        hint: t("circumscription.hints.rewind_weeks"),
      },
      forward_weeks: {
        label: t("activerecord.attributes.circumscription.forward_weeks"),
        hint: t("circumscription.hints.forward_weeks"),
      },
      min_speaker_re_req_months: {
        label: t("activerecord.attributes.circumscription.min_speaker_re_req_months"),
        hint: t("circumscription.hints.min_speaker_re_req_months"),
      },
      apply_sp_re_req_min_assign_count: {
        label: t("activerecord.attributes.circumscription.apply_sp_re_req_min_assign_count"),
        hint: t("circumscription.hints.apply_sp_re_req_min_assign_count"),
      },
      forward_sp_assign_weeks: {
        label: t("activerecord.attributes.circumscription.forward_sp_assign_weeks"),
        hint: t("circumscription.hints.forward_sp_assign_weeks"),
      },
      min_weeks_whitout_sp_assign: {
        label: t("activerecord.attributes.circumscription.min_weeks_whitout_sp_assign"),
        hint: t("circumscription.hints.min_weeks_whitout_sp_assign"),
      },
      max_weeks_whitout_sp_assign: {
        label: t("activerecord.attributes.circumscription.max_weeks_whitout_sp_assign"),
        hint: t("circumscription.hints.max_weeks_whitout_sp_assign"),
      },
      min_weeks_whitout_sp_assign_sm: {
        label: t("activerecord.attributes.circumscription.min_weeks_whitout_sp_assign_sm"),
        hint: t("circumscription.hints.min_weeks_whitout_sp_assign_sm"),
      },
      max_weeks_whitout_sp_assign_sm: {
        label: t("activerecord.attributes.circumscription.max_weeks_whitout_sp_assign_sm"),
        hint: t("circumscription.hints.max_weeks_whitout_sp_assign_sm"),
      },
    }),
    [t],
  );
}

type CircumscriptionFormValues = {
  circumscription_code: string;
  denomination: string;
  language_id: number;
} & Record<Parameter, number>;

/**
 * Sidebar entry "manage_circumsciptions" → /webmaster/circumscriptions.
 *
 * The circumscriptions themselves, which only the webmaster creates. Beyond the
 * name and the language, a circumscription carries the scheduling parameters
 * its congregations inherit — how far the planning window reaches in both
 * directions, and how long a speaker is left alone between two assignments. The
 * zone coordinator can refine some of them later on their own settings screen.
 *
 * Deactivating is refused while congregations or roles still hang off the
 * circumscription; the server answers 200 and says so in a notification.
 */
export function CircumscriptionManagementPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CircumscriptionFormValues>();
  const parameters = useParameterLabels();

  const [editing, setEditing] = useState<ManagedCircumscriptionRow | "new" | null>(null);

  const circumscriptions = useQuery({
    queryKey: ["webmaster", "circumscriptions"],
    queryFn: () =>
      apiFetch<ListResponse<ManagedCircumscriptionRow>>("/webmaster/circumscriptions"),
  });

  // The list names the language by id only, so the codes have to come from
  // here — the same query the editor's select uses.
  const languages = useQuery({
    queryKey: ["webmaster", "languages"],
    queryFn: () => apiFetch<ListResponse<LanguageRow>>("/webmaster/languages"),
    staleTime: Infinity,
  });

  const languageById = useMemo(() => {
    const map = new Map<number, LanguageRow>();
    (languages.data?.data ?? []).forEach((language) => map.set(language.id, language));
    return map;
  }, [languages.data]);

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["webmaster", "circumscriptions"] });
  }, [queryClient]);

  const save = useMutation({
    mutationFn: (values: CircumscriptionFormValues) =>
      editing === "new" || editing === null
        ? apiFetch<NotificationsResponse>("/webmaster/circumscriptions", {
            method: "POST",
            body: values,
          })
        : apiFetch<NotificationsResponse>(`/webmaster/circumscriptions/${editing.id}`, {
            method: "PUT",
            body: values,
          }),
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refresh();
    },
    onError: showError,
  });

  const deactivate = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/webmaster/circumscriptions/${id}`, { method: "DELETE" }),
    onSuccess: (data) => {
      // 200 even when it declined — "congregations are still assigned" arrives
      // as a notification.
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const openEditor = useCallback(
    (row: ManagedCircumscriptionRow | "new") => {
      setEditing(row);
      // resetFields before filling. The store no longer drops its values when
      // the dialog closes, so a field this handler does not write would keep
      // the previously edited row's value.
      form.resetFields();
      if (row === "new") return;

      form.setFieldsValue({
        circumscription_code: row.circumscription_code ?? "",
        denomination: row.denomination ?? "",
        language_id: row.language_id ?? undefined,
        ...Object.fromEntries(PARAMETERS.map((name) => [name, row[name] ?? undefined])),
      } as CircumscriptionFormValues);
    },
    [form],
  );

  const askAndDeactivate = useCallback(
    (row: ManagedCircumscriptionRow) => {
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: t("dialog.confirm_deactivate", {
          name: [row.circumscription_code, row.denomination].filter(Boolean).join(" · "),
        }),
        okText: t("dialog.answer_yes"),
        okButtonProps: { danger: true },
        cancelText: t("dialog.answer_no"),
        onOk: () => deactivate.mutate(row.id),
      });
    },
    [modal, t, deactivate],
  );

  const languageOptions = useMemo(
    () =>
      (languages.data?.data ?? []).map((language) => ({
        value: language.id,
        label: [language.language_code, language.denomination].filter(Boolean).join(" – "),
      })),
    [languages.data],
  );

  const busy = save.isPending || deactivate.isPending;

  const columns = useMemo<ExportableColumn<ManagedCircumscriptionRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.circumscription.circumscription_code"),
        dataIndex: "circumscription_code",
        width: 90,
        fixed: "left",
        sorter: (a, b) => compareText(a.circumscription_code, b.circumscription_code),
      },
      {
        title: t("activerecord.attributes.circumscription.denomination"),
        dataIndex: "denomination",
        width: 220,
        fixed: "left",
        defaultSortOrder: "ascend",
        sorter: (a, b) => compareText(a.denomination, b.denomination),
      },
      {
        title: t("activerecord.attributes.circumscription.language_id"),
        dataIndex: "language_id",
        width: 100,
        render: (value: number | null) =>
          value === null ? null : (languageById.get(value)?.language_code ?? value),
        // Without this the export would carry the id the list is keyed by
        // instead of the code the column shows.
        exportValue: (row) =>
          row.language_id === null
            ? null
            : (languageById.get(row.language_id)?.language_code ?? row.language_id),
      },
      ...PARAMETERS.map((name) => ({
        // The heading carries its hint, so it is markup rather than a string —
        // and a column the export cannot read a heading off is skipped. All
        // nine of them dropped out of the file that way.
        title: <Tooltip title={parameters[name].hint}>{parameters[name].label}</Tooltip>,
        exportHeader: parameters[name].label,
        dataIndex: name,
        width: 150,
        align: "right" as const,
      })),
      {
        key: "actions",
        fixed: "right" as const,
        width: 100,
        render: (_value: unknown, row: ManagedCircumscriptionRow) => (
          <Space size={4}>
            <Tooltip title={t("form.update")}>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={busy}
                onClick={() => openEditor(row)}
              />
            </Tooltip>
            <Tooltip title={t("circumscription.disable")}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={busy}
                onClick={() => askAndDeactivate(row)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [t, busy, parameters, languageById, openEditor, askAndDeactivate],
  );

  return (
    <Card
      title={t("navigation.manage_circumsciptions")}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor("new")}>
          {t("circumscription.create")}
        </Button>
      }
    >
      <DataTable<ManagedCircumscriptionRow>
        columns={columns}
        rows={circumscriptions.data?.data}
        exportAs={t("navigation.manage_circumsciptions")}
        loading={circumscriptions.isPending || busy}
        error={circumscriptions.error}
      />

      <Modal
        open={editing !== null}
        title={editing === "new" ? t("circumscription.create") : editing?.denomination}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: save.isPending }}
        onOk={() => form.submit()}
        width={720}
        destroyOnHidden
      >
        <Form<CircumscriptionFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
        >
          <Typography.Title level={5}>
            {t("circumscription.headers.circumscription_data")}
          </Typography.Title>

          <Space align="start" size="middle" wrap>
            <Form.Item
              name="circumscription_code"
              label={t("activerecord.attributes.circumscription.circumscription_code")}
              rules={[{ required: true }]}
            >
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="denomination"
              label={t("activerecord.attributes.circumscription.denomination")}
              rules={[{ required: true }]}
            >
              <Input style={{ width: 300 }} />
            </Form.Item>
            <Form.Item
              name="language_id"
              label={t("activerecord.attributes.circumscription.language_id")}
              extra={t("circumscription.hints.language_id")}
              // The server checks the language before anything else and answers
              // "language.not_valid"; asking here saves the round trip.
              rules={[{ required: true }]}
            >
              <Select
                options={languageOptions}
                loading={languages.isPending}
                style={{ width: 200 }}
              />
            </Form.Item>
          </Space>

          <Typography.Title level={5}>{t("circumscription.headers.settings")}</Typography.Title>

          <Space align="start" size="middle" wrap>
            {PARAMETERS.map((name) => (
              <Form.Item
                key={name}
                name={name}
                label={parameters[name].label}
                extra={
                  <span style={{ display: "inline-block", maxWidth: 300 }}>
                    {parameters[name].hint}
                  </span>
                }
                // Every one of them is NOT NULL with a database default, so an
                // emptied field would fail validation on the server.
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: 300 }} />
              </Form.Item>
            ))}
          </Space>
        </Form>
      </Modal>
    </Card>
  );
}
